
import React from "react";
import { cn } from "@/lib/utils";
import { NavItemType } from "./navItems";
import { SidebarLink } from "./SidebarLink";
import { Separator } from "@/components/ui/separator";

interface SidebarNavGroupProps {
  title?: string;
  items: NavItemType[];
  collapsed: boolean;
  currentPathname: string;
  onLinkClick?: () => void;
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  title,
  items,
  collapsed,
  currentPathname,
  onLinkClick,
}) => {
  if (items.length === 0) {
    return null;
  }

  // Filter out consecutive separators and leading/trailing separators
  const cleanedItems = items.reduce((acc, item, index, arr) => {
    if (item.type === 'separator') {
      // Skip if it's the first item or if the previous item was also a separator
      if (index === 0 || (acc.length > 0 && acc[acc.length - 1].type === 'separator')) {
        return acc;
      }
      // Skip if it's the last item and there's no link after it (or if next is also separator)
      const nextLinkItem = arr.slice(index + 1).find(i => i.type === 'link');
      if (!nextLinkItem && index < arr.length -1 && arr[index+1].type === 'separator') return acc;
      if (!nextLinkItem && index === arr.length -1) return acc;
    }
    acc.push(item);
    return acc;
  }, [] as NavItemType[]).filter((item, index, arr) => {
     // Remove trailing separator if it's the very last item
     if (item.type === 'separator' && index === arr.length -1) return false;
     return true;
  });

  if (cleanedItems.length === 0) return null;
  // If the only item left is a separator after cleaning, don't render the group.
  if (cleanedItems.length === 1 && cleanedItems[0].type === 'separator') return null;

  return (
    <nav className="space-y-1">
      {title && !collapsed && (
        <h3 className="px-2 text-xs font-semibold uppercase text-sidebar-foreground/70 tracking-wider">
          {title}
        </h3>
      )}
      {cleanedItems.map((item, index) => {
        if (item.type === "link") {
          return (
            <SidebarLink
              key={item.href || index}
              to={item.href}
              icon={item.icon}
              label={item.label}
              collapsed={collapsed}
              currentPathname={currentPathname}
              onLinkClick={onLinkClick}
            />
          );
        }
        if (item.type === "separator") {
          // Ensure separator is not rendered if it's the last item in the cleaned list
          if (index === cleanedItems.length - 1) return null;
          return <Separator key={`separator-${index}`} className={cn("my-2 bg-sidebar-border", collapsed ? "mx-2" : "mx-4")} />;
        }
        return null;
      })}
    </nav>
  );
};
